/**
 * @fileoverview Mock response helpers for the Sales CRM, Approvals and prospect metadata mocks.
 * @description Builds the backend's success and error envelopes, reads a mocked request's query,
 * body and headers, and pages a list, so the mocks answer exactly the way the real endpoints do
 * (`{ httpStatus, message, traceId, data, meta? }` on success, `{ httpStatus, appCode, message }` on
 * failure) and the shared response interceptor treats them the same.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type { AxiosRequestConfig } from 'axios'

/** @description A mock handler's reply: status, body and optional headers. */
export type MockReply = [number, object] | [number, object, Record<string, string>]

/**
 * @description Wraps data in the backend's success envelope.
 * @param {unknown} data - The payload.
 * @param {object} [meta] - Pagination meta, for a list.
 * @returns {object} The envelope.
 */
export function okEnvelope(data: unknown, meta?: object): object {
  return {
    httpStatus: 200,
    message: 'Success',
    traceId: crypto.randomUUID(),
    data,
    ...(meta && { meta }),
  }
}

/**
 * @description Builds an error reply in the backend's error envelope.
 * @param {number} httpStatus - The HTTP status.
 * @param {string} appCode - The backend's application error code.
 * @param {string} message - The backend's message.
 * @returns {MockReply} The reply.
 */
export function failReply(httpStatus: number, appCode: string, message: string): MockReply {
  return [
    httpStatus,
    { httpStatus, httpError: '', appCode, message, traceId: crypto.randomUUID(), data: [] },
  ]
}

/**
 * @description Reads a mocked request's query parameters.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {Record<string, string | undefined>} The parameters.
 */
export function queryOf(config: AxiosRequestConfig): Record<string, string | undefined> {
  const params = (config.params ?? {}) as Record<string, unknown>
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => [
      key,
      typeof value === 'string' || typeof value === 'number' ? String(value) : undefined,
    ]),
  )
}

/**
 * @description Reads a mocked request's JSON body.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {Record<string, unknown>} The body, or an empty object when there's none.
 */
export function bodyOf(config: AxiosRequestConfig): Record<string, unknown> {
  if (typeof config.data !== 'string' || config.data === '') return {}
  return JSON.parse(config.data) as Record<string, unknown>
}

/**
 * @description Reads one header off a mocked request, whichever way axios stored it.
 * @param {AxiosRequestConfig} config - The request config.
 * @param {string} name - The header's name.
 * @returns {string | undefined} Its value, if sent.
 */
export function headerOf(config: AxiosRequestConfig, name: string): string | undefined {
  const headers = config.headers as unknown
  const value =
    headers && typeof (headers as { get?: unknown }).get === 'function'
      ? (headers as { get: (key: string) => unknown }).get(name)
      : (headers as Record<string, unknown> | undefined)?.[name]
  return typeof value === 'string' ? value : undefined
}

/**
 * @description Answers one page of a list the way the backend does: `{ items }` plus top-level
 * pagination meta.
 * @param {AxiosRequestConfig} config - The request config, for `page` and `limit`.
 * @param {T[]} all - Every matching item.
 * @returns {MockReply} The page.
 */
export function pageReply<T>(config: AxiosRequestConfig, all: T[]): MockReply {
  const query = queryOf(config)
  const page = Math.max(1, Number(query.page ?? 1) || 1)
  const limit = Math.min(100, Math.max(1, Number(query.limit ?? 20) || 20))
  const items = all.slice((page - 1) * limit, page * limit)
  return [
    200,
    okEnvelope(
      { items },
      {
        page,
        limit,
        total: all.length,
        evaluatedAt: new Date().toISOString(),
      },
    ),
  ]
}
